import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ShoppingBag, Filter, Heart, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { shopifyFetch } from '@/shopify';
import { useCartStore, useWishlistStore } from '@/store';
import { toast } from 'sonner';
import { normalizeText, getSearchVariants } from '@/lib/search';

const ratingsCache = {};

async function fetchProductReviewStats(numericId, handle) {
  const cacheKey = numericId;

  if (ratingsCache[cacheKey] !== undefined) {
    return ratingsCache[cacheKey];
  }

  try {
    const lookupRes = await fetch(
      `/api/judgeme-product?action=lookup&external_id=${numericId}&handle=${handle}`
    );

    const lookupData = await lookupRes.json();

    if (!lookupData.ok || !lookupData.product?.id) {
      ratingsCache[cacheKey] = null;
      return null;
    }

    const internalId = lookupData.product.id;

    const reviewsRes = await fetch(
      `/api/judgeme-product?action=reviews&internal_id=${internalId}&page=1&per_page=100`
    );

    const reviewsData = await reviewsRes.json();

    if (!reviewsData.ok || !Array.isArray(reviewsData.reviews)) {
      ratingsCache[cacheKey] = null;
      return null;
    }

    const firstPageReviews = reviewsData.reviews;
    const PER_PAGE = 100;
    let count = firstPageReviews.length;

    if (firstPageReviews.length === PER_PAGE) {
      let page = 2;
      while (page <= 20) {
        const moreRes = await fetch(
          `/api/judgeme-product?action=reviews&internal_id=${internalId}&page=${page}&per_page=${PER_PAGE}`
        );
        const moreData = await moreRes.json();
        if (!moreData.ok || !Array.isArray(moreData.reviews) || moreData.reviews.length === 0) break;
        count += moreData.reviews.length;
        if (moreData.reviews.length < PER_PAGE) break;
        page++;
      }
    }

    const average =
      firstPageReviews.length > 0
        ? firstPageReviews.reduce((sum, r) => sum + Number(r.rating || 0), 0) / firstPageReviews.length
        : null;

    const result = count === 0 ? { average: null, count: 0 } : { average, count };

    ratingsCache[cacheKey] = result;
    return result;
  } catch {
    ratingsCache[cacheKey] = null;
    return null;
  }
}

async function loadRatingsInBatches(products, onBatchComplete, batchSize = 10) {
  for (let i = 0; i < products.length; i += batchSize) {
    const batch = products.slice(i, i + batchSize);

    const batchResults = await Promise.all(
      batch.map(async (product) => {
        if (product.rating !== null && product.review_count !== null) {
          return product;
        }
        const numericId = product.id.split('/').pop();
        const stats = await fetchProductReviewStats(numericId, product.handle);
        return {
          ...product,
          rating: stats ? stats.average : null,
          review_count: stats ? stats.count : 0
        };
      })
    );

    onBatchComplete(batchResults);
  }
}

const gridVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category') || 'all';
  const searchTerm = searchParams.get('search') || '';
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const { addItem } = useCartStore();
  const { toggleItem, isInWishlist } = useWishlistStore();

  useEffect(() => {
    async function fetchData() {
      setLoading(true);

      try {
        const data = await shopifyFetch(`
          query {
            products(first: 50) {
              edges {
                node {
                  id
                  title
                  handle
                  productType
                  availableForSale
                  featuredImage {
                    url
                    altText
                  }
                  priceRange {
                    minVariantPrice {
                      amount
                    }
                    maxVariantPrice {
                      amount
                    }
                  }
                  variants(first: 50) {
                    edges {
                      node {
                        id
                        title
                        availableForSale
                        price {
                          amount
                        }
                        compareAtPrice {
                          amount
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `);

        if (!data?.products?.edges) {
          setProducts([]);
          setCategories([]);
          setLoading(false);
          return;
        }

        const allProducts = data.products.edges.map(({ node }) => {
          const variants = node.variants?.edges?.map(({ node: variant }) => ({
            id: variant.id,
            title: variant.title,
            availableForSale: variant.availableForSale,
            price: parseFloat(variant.price.amount),
            compareAtPrice: variant.compareAtPrice?.amount
              ? parseFloat(variant.compareAtPrice.amount)
              : null
          })) || [];

          const availableVariants = variants.filter((variant) => variant.availableForSale);
          const variantsToUse = availableVariants.length > 0 ? availableVariants : variants;

          const cheapestVariant = variantsToUse.reduce((lowest, variant) => {
            if (!lowest || variant.price < lowest.price) return variant;
            return lowest;
          }, null);

          const discountedVariants = variantsToUse.filter(
            (variant) =>
              variant.compareAtPrice &&
              variant.compareAtPrice > variant.price
          );

          const cheapestDiscountedVariant = discountedVariants.reduce((lowest, variant) => {
            if (!lowest || variant.price < lowest.price) return variant;
            return lowest;
          }, null);

          const cheapestVariantHasDiscount =
            cheapestVariant?.compareAtPrice &&
            cheapestVariant.compareAtPrice > cheapestVariant.price;

          return {
            id: node.id,
            variantId: node.variants?.edges?.[0]?.node?.id ?? null,
            name: node.title,
            handle: node.handle,
            category: node.productType || 'Sem categoria',
            price: parseFloat(node.priceRange.minVariantPrice.amount),
            maxPrice: parseFloat(node.priceRange.maxVariantPrice.amount),
            variants,
            compareAtPrice: cheapestVariantHasDiscount
              ? cheapestVariant.compareAtPrice
              : null,
            hasVariantOnSale: discountedVariants.length > 0,
            saleVariantLabel: cheapestDiscountedVariant?.title || null,
            image_url: node.featuredImage?.url ?? '',
            stock: node.availableForSale ? 1 : 0,
            rating: null,
            review_count: null
          };
        });

        const allCategories = [...new Set(allProducts.map((p) => p.category))];

        let filtered = allProducts;

        if (selectedCategory !== 'all') {
          filtered = filtered.filter((p) => p.category === selectedCategory);
        }

        if (searchTerm) {
          const searchWords = normalizeText(searchTerm).split(' ').filter(Boolean);
          const variantsList = searchWords.map(getSearchVariants);

          filtered = filtered.filter((product) => {
            const name = normalizeText(product.name);
            const category = normalizeText(product.category);

            return variantsList.every((variants) =>
              variants.some(
                (variant) => name.includes(variant) || category.includes(variant)
              )
            );
          });
        }

        setProducts(filtered);
        setCategories(allCategories);
      } catch (error) {
        void error;
        toast.error('Erro ao carregar produtos');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [selectedCategory, searchTerm]);

  useEffect(() => {
    const hasMissingRatings = products.some(
      (p) => p.rating === null || p.review_count === null
    );

    if (!hasMissingRatings || products.length === 0) return;

    loadRatingsInBatches(products, (batchResults) => {
      setProducts((prev) => {
        const updated = [...prev];
        batchResults.forEach((p) => {
          const idx = updated.findIndex((u) => u.id === p.id);
          if (idx !== -1) updated[idx] = p;
        });
        return updated;
      });
    }, 5);
  }, [products.length, selectedCategory, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleAddToCart(product) {
    if (!product.variantId) {
      toast.error('Produto sem variante válida');
      return;
    }

    addItem(product);
    toast.success('Produto adicionado ao carrinho!');
  }

  function handleToggleWishlist(product, e) {
    e.preventDefault();
    e.stopPropagation();
    const wasInWishlist = isInWishlist(product.id);
    toggleItem(product);
    toast.success(wasInWishlist ? 'Removido da wishlist' : 'Adicionado à wishlist');
  }

  function handleCategoryChange(value) {
    const nextParams = new URLSearchParams(searchParams);

    if (value === 'all') {
      nextParams.delete('category');
    } else {
      nextParams.set('category', value);
    }

    setSearchParams(nextParams);
  }

  function clearFilters() {
    setSearchParams({});
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-6">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <h1 className="font-heading text-4xl md:text-6xl font-bold mb-4 text-text-primary">
            Nossa Coleção
          </h1>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <p className="font-body text-lg text-text-secondary">
              Descubra produtos que realçam sua beleza natural
            </p>

            {(searchTerm || selectedCategory !== 'all') && (
              <div className="flex items-center gap-3 text-sm text-text-secondary">
                <span>
                  {products.length} {products.length === 1 ? 'resultado' : 'resultados'}
                  {searchTerm ? ` para "${searchTerm}"` : ''}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  onClick={clearFilters}
                  className="h-9 rounded-full border-accent/30 text-accent hover:bg-accent hover:text-white"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpar
                </Button>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="mb-8"
        >
          <Select value={selectedCategory} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-full md:w-64">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="all">Todas as Categorias</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-gray-100 rounded mb-4" />
                <div className="space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-1/3" />
                  <div className="h-4 bg-gray-100 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                  <div className="h-6 bg-gray-100 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 text-text-secondary">
            <p className="mb-6">Nenhum produto encontrado</p>
            {(searchTerm || selectedCategory !== 'all') && (
              <Button
                type="button"
                onClick={clearFilters}
                className="bg-accent text-white hover:bg-accent/90 rounded-full px-8"
              >
                Ver todos os produtos
              </Button>
            )}
          </div>
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={gridVariants}
            className="grid grid-cols-1 md:grid-cols-4 gap-8"
          >
            {products.map((product) => (
              <motion.div
                key={product.id}
                variants={cardVariants}
                transition={{ duration: 0.4 }}
                className="group"
              >
                <Link to={`/produto/${encodeURIComponent(product.id)}`}>
                  <div className="relative aspect-[3/4] overflow-hidden mb-4 bg-gray-100">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />

                    <button
                      onClick={(e) => handleToggleWishlist(product, e)}
                      aria-label={
                        isInWishlist(product.id)
                          ? 'Remover da wishlist'
                          : 'Adicionar à wishlist'
                      }
                      className="absolute top-4 right-4 bg-white/90 hover:bg-white rounded-full p-2 shadow-sm transition z-10 cursor-pointer"
                    >
                      <Heart
                        className={`w-5 h-5 ${
                          isInWishlist(product.id)
                            ? 'fill-accent text-accent'
                            : 'text-text-primary'
                        }`}
                      />
                    </button>

                    {product.stock === 0 && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="text-white font-semibold">Esgotado</span>
                      </div>
                    )}
                  </div>
                </Link>

                <div className="space-y-2">
                  <p className="text-xs text-text-muted uppercase tracking-widest">
                    {product.category}
                  </p>

                  <Link to={`/produto/${encodeURIComponent(product.id)}`}>
                    <h3 className="font-body font-semibold hover:text-accent transition-colors">
                      {product.name}
                    </h3>
                  </Link>

                  {product.review_count === null ? (
                    <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
                  ) : product.review_count > 0 && product.rating !== null ? (
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center">
                        <Star className="w-4 h-4 fill-accent text-accent" />
                        <span className="ml-1 text-sm text-text-secondary">
                          {product.rating.toFixed(1)}
                        </span>
                      </div>
                      <span className="text-sm text-text-muted">
                        ({product.review_count})
                      </span>
                    </div>
                  ) : null}

                  <div className="flex items-center justify-between pt-2">
                    <div className="flex flex-col">
                      {product.compareAtPrice && product.compareAtPrice > product.price && (
                        <span className="text-sm text-text-muted line-through">
                          {product.compareAtPrice.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          })}
                        </span>
                      )}

                      <span className="font-heading text-xl font-bold text-accent">
                        {product.maxPrice && product.maxPrice !== product.price
                          ? `A partir de ${product.price.toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL'
                            })}`
                          : product.price.toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL'
                            })}
                      </span>

                      {!product.compareAtPrice && product.hasVariantOnSale && (
                        <span className="mt-1 w-fit rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-accent">
                          Opção em oferta
                        </span>
                      )}
                    </div>

                    <Button
                      onClick={() => handleAddToCart(product)}
                      disabled={product.stock === 0 || !product.variantId}
                      aria-label={`Adicionar ${product.name} ao carrinho`}
                      className="bg-accent text-white hover:bg-accent/90 rounded-full p-2 transition-all duration-300 hover:shadow-lg"
                    >
                      <ShoppingBag className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
